import { useRef } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

import type { Transform } from './types';

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CanvasScrollbarsProps {
  viewport: { width: number; height: number };
  transform: Transform;
  /** World-space rect of the content (the plan); the scrollable range is its union with the visible area. */
  contentRect: Rect;
  onTransformChange: (t: Transform) => void;
}

type Axis = 'x' | 'y';

interface ThumbDrag {
  pointerId: number;
  startClient: number;
  startOffset: number;
  worldPerTrackPx: number;
}

const MIN_THUMB_PX = 32;

function axisRange(viewStart: number, viewSize: number, contentStart: number, contentSize: number) {
  const start = Math.min(viewStart, contentStart);
  const end = Math.max(viewStart + viewSize, contentStart + contentSize);
  const total = end - start;
  return {
    total,
    thumbStart: (viewStart - start) / total,
    thumbSize: viewSize / total,
  };
}

/**
 * Scrollbars for the pan/zoom canvas: dragging a thumb pans the viewport the
 * same way a document scrollbar would. Shown only on an axis where the plan
 * extends past the visible area. On touch screens they sit left of the
 * editor's page-scroll strip.
 */
export function CanvasScrollbars({ viewport, transform, contentRect, onTransformChange }: CanvasScrollbarsProps) {
  const trackRefs = { x: useRef<HTMLDivElement>(null), y: useRef<HTMLDivElement>(null) };
  const drag = useRef<ThumbDrag | null>(null);

  const view = {
    x: -transform.offsetX / transform.scale,
    y: -transform.offsetY / transform.scale,
    width: viewport.width / transform.scale,
    height: viewport.height / transform.scale,
  };
  const ranges = {
    x: axisRange(view.x, view.width, contentRect.x, contentRect.width),
    y: axisRange(view.y, view.height, contentRect.y, contentRect.height),
  };
  const visible = {
    x: ranges.x.thumbSize < 0.999,
    y: ranges.y.thumbSize < 0.999,
  };

  const onThumbPointerDown = (axis: Axis, e: ReactPointerEvent<HTMLDivElement>) => {
    const track = trackRefs[axis].current;
    if (!track) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    const trackPx = axis === 'x' ? track.clientWidth : track.clientHeight;
    drag.current = {
      pointerId: e.pointerId,
      startClient: axis === 'x' ? e.clientX : e.clientY,
      startOffset: axis === 'x' ? transform.offsetX : transform.offsetY,
      worldPerTrackPx: ranges[axis].total / Math.max(1, trackPx),
    };
  };

  const onThumbPointerMove = (axis: Axis, e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const deltaPx = (axis === 'x' ? e.clientX : e.clientY) - d.startClient;
    const offset = d.startOffset - deltaPx * d.worldPerTrackPx * transform.scale;
    onTransformChange(axis === 'x' ? { ...transform, offsetX: offset } : { ...transform, offsetY: offset });
  };

  const onThumbPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointerId === e.pointerId) drag.current = null;
  };

  const thumbStyle = (axis: Axis) => {
    const { thumbStart, thumbSize } = ranges[axis];
    const size = `max(${MIN_THUMB_PX}px, ${thumbSize * 100}%)`;
    // Keep the thumb inside the track even when MIN_THUMB_PX inflates it.
    const start = `calc((100% - ${size}) * ${Math.min(1, Math.max(0, thumbStart / Math.max(0.0001, 1 - thumbSize)))})`;
    return axis === 'x' ? { width: size, left: start } : { height: size, top: start };
  };

  const thumb = (axis: Axis) => (
    <div
      className="absolute touch-none rounded-full bg-ink-4/50 hover:bg-ink-4/70 active:bg-accent/60"
      style={{ ...thumbStyle(axis), ...(axis === 'x' ? { top: 3, bottom: 3 } : { left: 3, right: 3 }) }}
      onPointerDown={e => onThumbPointerDown(axis, e)}
      onPointerMove={e => onThumbPointerMove(axis, e)}
      onPointerUp={onThumbPointerUp}
      onPointerCancel={onThumbPointerUp}
    />
  );

  return (
    <>
      {visible.x && (
        <div
          ref={trackRefs.x}
          className="no-print absolute bottom-0 left-0 z-10 h-3.5 right-3.5 [@media(pointer:coarse)]:right-[2.625rem]"
          onClick={e => e.stopPropagation()}
        >
          {thumb('x')}
        </div>
      )}
      {visible.y && (
        <div
          ref={trackRefs.y}
          className="no-print absolute right-0 top-0 z-10 w-3.5 bottom-3.5 [@media(pointer:coarse)]:right-7"
          onClick={e => e.stopPropagation()}
        >
          {thumb('y')}
        </div>
      )}
    </>
  );
}
